import React from 'react';

interface SkeletonProps {
  className?: string;
  height?: string;
  width?: string;
  rounded?: boolean;
}

export const Skeleton: React.FC<SkeletonProps> = ({ 
  className = '', 
  height = 'h-4', 
  width = 'w-full', 
  rounded = true 
}) => {
  return (
    <div 
      className={`animate-pulse bg-gray-200 dark:bg-gray-700 ${height} ${width} ${
        rounded ? 'rounded' : ''
      } ${className}`}
    />
  );
};

// Page Skeleton Components
export const PageSkeleton: React.FC = () => (
  <div className="pt-24">
    {/* Hero Section Skeleton */}
    <section className="relative bg-gradient-to-r from-blue-900 to-purple-900 py-20">
      <div className="container-fmc relative z-10 text-center">
        <Skeleton className="h-16 w-96 mx-auto mb-6" />
        <Skeleton className="h-8 w-3/4 mx-auto" />
      </div>
    </section>

    {/* Content Section Skeleton */}
    <section className="py-20 bg-white dark:bg-gray-900">
      <div className="container-fmc">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left Column */}
          <div className="space-y-8">
            <div>
              <Skeleton className="h-10 w-3/4 mb-4" />
              <Skeleton className="h-6 w-full mb-2" />
              <Skeleton className="h-6 w-5/6" />
            </div>
            
            {/* Form Skeleton */}
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-12 w-32" />
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-8">
            <div>
              <Skeleton className="h-8 w-48 mb-6" />
              <div className="space-y-6">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-start space-x-4">
                    <Skeleton className="w-12 h-12 rounded-lg flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-5 w-32" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-3/4" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Contact Card Skeleton */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6">
              <Skeleton className="h-6 w-40 mb-3" />
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-5/6 mb-4" />
              <div className="flex items-center space-x-3">
                <Skeleton className="w-5 h-5" />
                <Skeleton className="h-5 w-48" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
);

export const ProjectsSkeleton: React.FC = () => (
  <div className="pt-24">
    {/* Hero Section Skeleton */}
    <section className="relative bg-gradient-to-r from-blue-900 to-purple-900 py-20">
      <div className="container-fmc relative z-10 text-center">
        <Skeleton className="h-16 w-96 mx-auto mb-6" />
        <Skeleton className="h-8 w-3/4 mx-auto" />
      </div>
    </section>

    {/* Search and Filters Skeleton */}
    <section className="py-20 bg-white dark:bg-gray-900">
      <div className="container-fmc">
        <div className="flex flex-col xl:flex-row gap-6 items-start justify-between">
          <div className="flex flex-col lg:flex-row gap-4 items-start w-full xl:w-auto">
            <div className="relative w-full lg:w-80">
              <Skeleton className="h-4 w-16 mb-1" />
              <Skeleton className="h-12 w-full rounded-lg" />
            </div>
            <div className="relative w-full lg:w-48">
              <Skeleton className="h-4 w-20 mb-1" />
              <Skeleton className="h-12 w-full rounded-lg" />
            </div>
            <div className="relative w-full lg:w-48">
              <Skeleton className="h-4 w-16 mb-1" />
              <Skeleton className="h-12 w-full rounded-lg" />
            </div>
            <div className="relative w-full lg:w-48">
              <Skeleton className="h-4 w-20 mb-1" />
              <Skeleton className="h-12 w-full rounded-lg" />
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Skeleton className="h-5 w-32" />
            <div className="flex items-center bg-white dark:bg-gray-700 rounded-lg p-1">
              <Skeleton className="w-16 h-8 rounded-md" />
              <Skeleton className="w-16 h-8 rounded-md" />
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Projects Grid Skeleton */}
    <section className="py-20 bg-white dark:bg-gray-900">
      <div className="container-fmc">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
              <div className="mb-6">
                <Skeleton className="w-full h-48 rounded-xl mb-4" />
                <div className="flex items-center justify-between mb-2">
                  <Skeleton className="h-6 w-24 rounded-full" />
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-5 w-20 rounded-full" />
                    <Skeleton className="h-4 w-16" />
                  </div>
                </div>
                <Skeleton className="h-6 w-3/4 mb-2" />
                <Skeleton className="h-4 w-full mb-1" />
                <Skeleton className="h-4 w-5/6" />
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Skeleton className="h-4 w-20 mb-1" />
                    <Skeleton className="h-5 w-24" />
                  </div>
                  <div>
                    <Skeleton className="h-4 w-16 mb-1" />
                    <Skeleton className="h-5 w-20" />
                  </div>
                  <div>
                    <Skeleton className="h-4 w-20 mb-1" />
                    <Skeleton className="h-5 w-28" />
                  </div>
                </div>
                
                <div>
                  <Skeleton className="h-5 w-32 mb-3" />
                  <div className="space-y-2">
                    {[1, 2, 3].map((j) => (
                      <div key={j} className="flex items-center space-x-2">
                        <Skeleton className="w-4 h-4 rounded-full" />
                        <Skeleton className="h-4 w-32" />
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <Skeleton className="h-12 w-full rounded-xl" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  </div>
);

export const BlogSkeleton: React.FC = () => (
  <div className="pt-24">
    {/* Hero Section Skeleton */}
    <section className="relative bg-gradient-to-r from-blue-900 to-purple-900 py-20">
      <div className="container-fmc relative z-10 text-center">
        <Skeleton className="h-16 w-96 mx-auto mb-6" />
        <Skeleton className="h-8 w-3/4 mx-auto" />
      </div>
    </section>

    {/* Search and Filters Skeleton */}
    <section className="py-20 bg-white dark:bg-gray-900">
      <div className="container-fmc">
        <div className="flex flex-col xl:flex-row gap-6 items-start justify-between">
          <div className="flex flex-col lg:flex-row gap-4 items-start w-full xl:w-auto">
            <div className="relative w-full lg:w-80">
              <Skeleton className="h-4 w-16 mb-1" />
              <Skeleton className="h-12 w-full rounded-lg" />
            </div>
            <div className="relative w-full lg:w-48">
              <Skeleton className="h-4 w-20 mb-1" />
              <Skeleton className="h-12 w-full rounded-lg" />
            </div>
            <div className="relative w-full lg:w-48">
              <Skeleton className="h-4 w-16 mb-1" />
              <Skeleton className="h-12 w-full rounded-lg" />
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Skeleton className="h-5 w-32" />
            <div className="flex items-center bg-white dark:bg-gray-700 rounded-lg p-1">
              <Skeleton className="w-16 h-8 rounded-md" />
              <Skeleton className="w-16 h-8 rounded-md" />
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Articles Grid Skeleton */}
    <section className="py-20 bg-white dark:bg-gray-900">
      <div className="container-fmc">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <article key={i} className="group bg-white dark:bg-gray-800 rounded-3xl shadow-xl overflow-hidden">
              <Skeleton className="w-full h-56 rounded-t-3xl" />
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <Skeleton className="h-5 w-20 rounded-full" />
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-4 h-4" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                </div>
                
                <Skeleton className="h-6 w-3/4 mb-3" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-5/6 mb-4" />
                
                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 py-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Skeleton className="w-4 h-4" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                    <div className="flex items-center gap-2">
                      <Skeleton className="w-4 h-4" />
                      <Skeleton className="h-4 w-20" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-4 h-4" />
                    <Skeleton className="h-4 w-16" />
                  </div>
                </div>
                
                <div className="flex items-center justify-between pt-4">
                  <Skeleton className="h-10 w-28 rounded-xl" />
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-10 h-10 rounded-xl" />
                    <Skeleton className="w-10 h-10 rounded-xl" />
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  </div>
);

export const SectorsSkeleton: React.FC = () => (
  <div className="pt-24">
    {/* Hero Section Skeleton */}
    <section className="relative bg-gradient-to-r from-blue-900 to-purple-900 py-20">
      <div className="container-fmc relative z-10 text-center">
        <Skeleton className="h-16 w-96 mx-auto mb-6" />
        <Skeleton className="h-8 w-3/4 mx-auto" />
      </div>
    </section>

    {/* Sectors Grid Skeleton */}
    <section className="py-20 bg-white dark:bg-gray-900">
      <div className="container-fmc">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg border border-gray-100 dark:border-gray-700">
              {/* Icon Skeleton */}
              <Skeleton className="w-16 h-16 rounded-2xl mb-6" />
              
              {/* Content Skeleton */}
              <div className="space-y-4">
                <Skeleton className="h-8 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-4/5" />
                
                <div className="flex items-center justify-between pt-4">
                  <Skeleton className="h-4 w-24" />
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Call to Action Skeleton */}
    <section className="py-20 bg-gray-50 dark:bg-gray-800">
      <div className="container-fmc text-center">
        <Skeleton className="h-12 w-3/4 mx-auto mb-6" />
        <Skeleton className="h-6 w-2/3 mx-auto mb-2" />
        <Skeleton className="h-6 w-1/2 mx-auto mb-8" />
        <Skeleton className="h-12 w-48 mx-auto" />
      </div>
    </section>
  </div>
);

export const CSRSkeleton: React.FC = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900" style={{ paddingTop: 'var(--header-offset, 96px)' }}>
    {/* Hero Section Skeleton */}
    <section className="relative py-20 min-h-[70vh] flex items-center bg-gradient-to-r from-blue-900 to-blue-800">
      <div className="container-fmc relative z-10 text-center">
        <Skeleton className="w-24 h-24 rounded-3xl mx-auto mb-6 bg-gray-700" />
        <Skeleton className="h-16 w-3/4 md:w-2/3 mx-auto mb-6 bg-gray-700" />
        <Skeleton className="h-8 w-2/3 md:w-1/2 mx-auto bg-gray-700" />
      </div>
    </section>

    {/* Mission & Vision Section Skeleton */}
    <section className="py-16 bg-white dark:bg-gray-800">
      <div className="container-fmc">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Mission Column */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <Skeleton className="w-8 h-8 rounded" />
              <Skeleton className="h-8 w-48" />
            </div>
            <Skeleton className="h-6 w-full mb-2" />
            <Skeleton className="h-6 w-5/6 mb-2" />
            <Skeleton className="h-6 w-4/5 mb-6" />
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6">
              <Skeleton className="h-6 w-32 mb-3" />
              <div className="space-y-2">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-4 w-3/4" />
                ))}
              </div>
            </div>
          </div>

          {/* Vision Column */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <Skeleton className="w-8 h-8 rounded" />
              <Skeleton className="h-8 w-48" />
            </div>
            <Skeleton className="h-6 w-full mb-2" />
            <Skeleton className="h-6 w-5/6 mb-2" />
            <Skeleton className="h-6 w-4/5 mb-6" />
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-6">
              <Skeleton className="h-6 w-32 mb-3" />
              <div className="space-y-2">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-4 w-3/4" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Achievements Section Skeleton */}
    <section className="py-16 bg-gray-50 dark:bg-gray-900">
      <div className="container-fmc">
        <div className="text-center mb-12">
          <Skeleton className="h-10 w-96 mx-auto mb-4" />
          <Skeleton className="h-6 w-2/3 mx-auto" />
        </div>
        <div className="grid md:grid-cols-4 gap-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="text-center bg-white dark:bg-gray-800 rounded-xl p-8 shadow-lg">
              <Skeleton className="w-16 h-16 rounded-full mx-auto mb-4" />
              <Skeleton className="h-8 w-20 mx-auto mb-2" />
              <Skeleton className="h-5 w-32 mx-auto" />
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CSR Initiatives Section Skeleton */}
    <section className="py-16 bg-white dark:bg-gray-800">
      <div className="container-fmc">
        <div className="text-center mb-12">
          <Skeleton className="h-10 w-96 mx-auto mb-4" />
          <Skeleton className="h-6 w-2/3 mx-auto" />
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
              <div className="flex items-center gap-4 mb-6">
                <Skeleton className="w-12 h-12 rounded-lg" />
                <Skeleton className="h-7 w-48" />
              </div>
              <Skeleton className="h-5 w-full mb-2" />
              <Skeleton className="h-5 w-5/6 mb-6" />
              <div className="space-y-3">
                {[1, 2, 3, 4].map((j) => (
                  <div key={j} className="flex items-center gap-2">
                    <Skeleton className="w-2 h-2 rounded-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Call to Action Section Skeleton */}
    <section className="py-20 bg-gradient-to-r from-blue-900 to-purple-900">
      <div className="container-fmc">
        <div className="max-w-3xl mx-auto text-center">
          <Skeleton className="h-12 w-3/4 mx-auto mb-6 bg-gray-700" />
          <Skeleton className="h-6 w-2/3 mx-auto mb-2 bg-gray-700" />
          <Skeleton className="h-6 w-1/2 mx-auto mb-8 bg-gray-700" />
          <div className="flex flex-col md:flex-row gap-4 justify-center">
            <Skeleton className="h-12 w-48 rounded-xl bg-gray-700" />
            <Skeleton className="h-12 w-48 rounded-xl bg-gray-700" />
          </div>
        </div>
      </div>
    </section>
  </div>
);

// Reusable skeleton for policy/commitment pages (Code of Ethics, HSE, Quality Management)
export const PolicyPageSkeleton: React.FC = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900" style={{ paddingTop: 'var(--header-offset, 96px)' }}>
    {/* Hero Section Skeleton */}
    <section className="relative py-20 bg-gradient-to-r from-blue-900 to-purple-900">
      <div className="container-fmc relative z-10 text-center">
        <Skeleton className="w-24 h-24 rounded-3xl mx-auto mb-6 bg-gray-700" />
        <Skeleton className="h-16 w-3/4 md:w-2/3 mx-auto mb-6 bg-gray-700" />
        <Skeleton className="h-8 w-2/3 md:w-1/2 mx-auto bg-gray-700" />
      </div>
    </section>

    {/* Commitment/Introduction Section Skeleton */}
    <section className="py-16 bg-white dark:bg-gray-800">
      <div className="container-fmc">
        <div className="max-w-4xl mx-auto text-center">
          <Skeleton className="h-10 w-96 mx-auto mb-6" />
          <Skeleton className="h-6 w-full mb-2" />
          <Skeleton className="h-6 w-5/6 mx-auto mb-2" />
          <Skeleton className="h-6 w-4/5 mx-auto" />
        </div>
      </div>
    </section>

    {/* Main Content Cards Section Skeleton */}
    <section className="py-16 bg-gray-50 dark:bg-gray-900">
      <div className="container-fmc">
        <div className="text-center mb-12">
          <Skeleton className="h-10 w-96 mx-auto mb-4" />
          <Skeleton className="h-6 w-2/3 mx-auto" />
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg">
              <div className="flex items-center gap-4 mb-6">
                <Skeleton className="w-12 h-12 rounded-lg" />
                <Skeleton className="h-7 w-48" />
              </div>
              <Skeleton className="h-5 w-full mb-2" />
              <Skeleton className="h-5 w-5/6 mb-2" />
              <Skeleton className="h-5 w-4/5" />
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Additional Content Section Skeleton */}
    <section className="py-16 bg-white dark:bg-gray-800">
      <div className="container-fmc">
        <div className="text-center mb-12">
          <Skeleton className="h-10 w-96 mx-auto mb-4" />
          <Skeleton className="h-6 w-2/3 mx-auto" />
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
              <Skeleton className="w-16 h-16 rounded-full mx-auto mb-4" />
              <Skeleton className="h-6 w-3/4 mx-auto mb-3" />
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-5/6 mx-auto" />
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA Section Skeleton */}
    <section className="py-20 bg-gradient-to-r from-blue-900 to-purple-900">
      <div className="container-fmc">
        <div className="max-w-3xl mx-auto text-center">
          <Skeleton className="h-12 w-3/4 mx-auto mb-6 bg-gray-700" />
          <Skeleton className="h-6 w-2/3 mx-auto mb-8 bg-gray-700" />
          <Skeleton className="h-12 w-48 mx-auto rounded-xl bg-gray-700" />
        </div>
      </div>
    </section>
  </div>
);

// Skeleton for Clients and Vendors pages (logo grid pages)
export const LogoGridPageSkeleton: React.FC = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900" style={{ paddingTop: 'var(--header-offset, 96px)' }}>
    {/* Hero Section Skeleton */}
    <section className="relative py-20 bg-gradient-to-r from-blue-900 to-purple-900">
      <div className="container-fmc relative z-10 text-center">
        <Skeleton className="h-16 w-3/4 md:w-2/3 mx-auto mb-6 bg-gray-700" />
        <Skeleton className="h-8 w-2/3 md:w-1/2 mx-auto mb-8 bg-gray-700" />
        <Skeleton className="h-12 w-48 mx-auto rounded-xl bg-gray-700" />
      </div>
    </section>

    {/* Statistics Section Skeleton */}
    <section className="py-16 bg-white dark:bg-gray-800">
      <div className="container-fmc">
        <div className="grid md:grid-cols-4 gap-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="text-center">
              <Skeleton className="h-12 w-24 mx-auto mb-2" />
              <Skeleton className="h-5 w-32 mx-auto" />
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Logo Grid Section Skeleton */}
    <section className="py-16 bg-gray-50 dark:bg-gray-900">
      <div className="container-fmc">
        <div className="text-center mb-12">
          <Skeleton className="h-10 w-96 mx-auto mb-4" />
          <Skeleton className="h-6 w-2/3 mx-auto" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-8">
          {[...Array(20)].map((_, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
              <Skeleton className="w-full h-32 rounded-xl" />
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA Section Skeleton */}
    <section className="py-20 bg-gradient-to-r from-blue-900 to-purple-900">
      <div className="container-fmc">
        <div className="max-w-3xl mx-auto text-center">
          <Skeleton className="h-12 w-3/4 mx-auto mb-6 bg-gray-700" />
          <Skeleton className="h-6 w-2/3 mx-auto mb-8 bg-gray-700" />
          <div className="flex flex-col md:flex-row gap-4 justify-center">
            <Skeleton className="h-12 w-48 rounded-xl bg-gray-700" />
            <Skeleton className="h-12 w-48 rounded-xl bg-gray-700" />
          </div>
        </div>
      </div>
    </section>
  </div>
);

// Skeleton for individual Service pages
export const ServicePageSkeleton: React.FC = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900" style={{ paddingTop: 'var(--header-offset, 96px)' }}>
    {/* Hero Section Skeleton */}
    <section className="relative py-20 bg-gradient-to-r from-blue-900 to-purple-900">
      <div className="container-fmc relative z-10 text-center">
        <Skeleton className="w-20 h-20 rounded-2xl mx-auto mb-6 bg-gray-700" />
        <Skeleton className="h-16 w-3/4 md:w-2/3 mx-auto mb-6 bg-gray-700" />
        <Skeleton className="h-8 w-2/3 md:w-1/2 mx-auto bg-gray-700" />
      </div>
    </section>

    {/* Service Overview Section Skeleton */}
    <section className="py-16 bg-white dark:bg-gray-800">
      <div className="container-fmc">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <Skeleton className="h-10 w-3/4 mb-4" />
            <Skeleton className="h-6 w-full mb-2" />
            <Skeleton className="h-6 w-5/6 mb-2" />
            <Skeleton className="h-6 w-4/5 mb-6" />
            <div className="grid grid-cols-2 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                  <Skeleton className="h-10 w-16 mb-2" />
                  <Skeleton className="h-4 w-24" />
                </div>
              ))}
            </div>
          </div>
          <Skeleton className="w-full h-96 rounded-2xl" />
        </div>
      </div>
    </section>

    {/* Services Offered Section Skeleton */}
    <section className="py-16 bg-gray-50 dark:bg-gray-900">
      <div className="container-fmc">
        <div className="text-center mb-12">
          <Skeleton className="h-10 w-96 mx-auto mb-4" />
          <Skeleton className="h-6 w-2/3 mx-auto" />
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
              <Skeleton className="w-12 h-12 rounded-xl mb-4" />
              <Skeleton className="h-6 w-3/4 mb-3" />
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-5/6" />
            </div>
          ))}
        </div>
      </div>
    </section>
  </div>
);

// Skeleton for individual Sector pages (same structure as service pages)
export const SectorPageSkeleton: React.FC = () => <ServicePageSkeleton />;

// Skeleton for Contact page
export const ContactPageSkeleton: React.FC = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900" style={{ paddingTop: 'var(--header-offset, 96px)' }}>
    {/* Hero Section Skeleton */}
    <section className="relative py-20 bg-gradient-to-r from-blue-900 to-purple-900">
      <div className="container-fmc relative z-10 text-center">
        <Skeleton className="h-16 w-3/4 md:w-2/3 mx-auto mb-6 bg-gray-700" />
        <Skeleton className="h-8 w-2/3 md:w-1/2 mx-auto bg-gray-700" />
      </div>
    </section>

    {/* Contact Form & Info Section Skeleton */}
    <section className="py-20 bg-white dark:bg-gray-900">
      <div className="container-fmc">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Form Column */}
          <div className="space-y-6">
            <Skeleton className="h-10 w-3/4 mb-6" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Skeleton className="h-12 w-full rounded-lg" />
              <Skeleton className="h-12 w-full rounded-lg" />
            </div>
            <Skeleton className="h-12 w-full rounded-lg" />
            <Skeleton className="h-12 w-full rounded-lg" />
            <Skeleton className="h-32 w-full rounded-lg" />
            <Skeleton className="h-12 w-32 rounded-xl" />
          </div>

          {/* Info Column */}
          <div className="space-y-8">
            <Skeleton className="h-8 w-48 mb-6" />
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-start space-x-4">
                <Skeleton className="w-12 h-12 rounded-lg flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* Map Section Skeleton */}
    <section className="py-16 bg-gray-50 dark:bg-gray-800">
      <div className="container-fmc">
        <Skeleton className="w-full h-96 rounded-2xl" />
      </div>
    </section>
  </div>
);

// Skeleton for Blog listing page
export const BlogPageSkeleton: React.FC = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900" style={{ paddingTop: 'var(--header-offset, 96px)' }}>
    {/* Hero Section Skeleton */}
    <section className="relative py-20 bg-gradient-to-r from-blue-900 to-purple-900">
      <div className="container-fmc relative z-10 text-center">
        <Skeleton className="h-16 w-3/4 md:w-2/3 mx-auto mb-6 bg-gray-700" />
        <Skeleton className="h-8 w-2/3 md:w-1/2 mx-auto bg-gray-700" />
      </div>
    </section>

    {/* Filters Section Skeleton */}
    <section className="py-8 bg-white dark:bg-gray-800">
      <div className="container-fmc">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-10 w-24 rounded-xl" />
            ))}
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-10 w-10 rounded-xl" />
            <Skeleton className="h-10 w-10 rounded-xl" />
            <Skeleton className="h-10 w-10 rounded-xl" />
          </div>
        </div>
      </div>
    </section>

    {/* Blog Grid Section Skeleton */}
    <section className="py-16 bg-gray-50 dark:bg-gray-900">
      <div className="container-fmc">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
            <article key={i} className="bg-white dark:bg-gray-800 rounded-3xl overflow-hidden shadow-lg">
              <Skeleton className="w-full h-56" />
              <div className="p-6">
                <Skeleton className="h-5 w-24 rounded-full mb-3" />
                <Skeleton className="h-6 w-full mb-3" />
                <Skeleton className="h-6 w-3/4 mb-4" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-5/6 mb-4" />
                <div className="flex items-center justify-between">
                  <Skeleton className="h-10 w-28 rounded-xl" />
                  <Skeleton className="h-4 w-24" />
                </div>
              </div>
            </article>
          ))}
        </div>
        
        {/* Pagination Skeleton */}
        <div className="flex justify-center items-center gap-2 mt-12">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="w-10 h-10 rounded-lg" />
          ))}
        </div>
      </div>
    </section>
  </div>
);

// Skeleton for Single Blog Post page
export const SingleBlogPostSkeleton: React.FC = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900" style={{ paddingTop: 'var(--header-offset, 96px)' }}>
    <div className="container-fmc py-12">
      <div className="max-w-4xl mx-auto">
        {/* Back Button Skeleton */}
        <div className="mb-8">
          <Skeleton className="h-10 w-32 rounded-xl" />
        </div>

        {/* Article Header Skeleton */}
        <article className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl overflow-hidden">
          {/* Category & Title */}
          <div className="p-8 pb-0">
            <Skeleton className="h-6 w-32 rounded-full mb-4" />
            <Skeleton className="h-12 w-full mb-4" />
            <Skeleton className="h-12 w-3/4 mb-6" />
            
            {/* Meta Info */}
            <div className="flex items-center gap-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-5 w-28" />
            </div>
          </div>

          {/* Featured Image */}
          <div className="p-8">
            <Skeleton className="w-full h-96 rounded-2xl" />
          </div>

          {/* Article Content */}
          <div className="p-8 space-y-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-5 w-full" />
                <Skeleton className="h-5 w-full" />
                <Skeleton className="h-5 w-5/6" />
              </div>
            ))}
          </div>

          {/* Tags Section */}
          <div className="p-8 pt-0">
            <div className="flex gap-2 flex-wrap">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-8 w-20 rounded-full" />
              ))}
            </div>
          </div>
        </article>
      </div>
    </div>
  </div>
);

// Skeleton for Single Project page
export const SingleProjectSkeleton: React.FC = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900" style={{ paddingTop: 'var(--header-offset, 96px)' }}>
    {/* Hero Section Skeleton */}
    <section className="relative py-20 bg-gradient-to-r from-blue-900 to-purple-900">
      <div className="container-fmc relative z-10">
        <div className="mb-8">
          <Skeleton className="h-10 w-32 rounded-xl bg-gray-700" />
        </div>
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <Skeleton className="h-16 w-full mb-4 bg-gray-700" />
            <Skeleton className="h-8 w-3/4 bg-gray-700" />
            <div className="grid grid-cols-2 gap-4 mt-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white/10 rounded-lg p-4">
                  <Skeleton className="h-8 w-20 mb-2 bg-gray-700" />
                  <Skeleton className="h-4 w-24 bg-gray-700" />
                </div>
              ))}
            </div>
          </div>
          <Skeleton className="w-full h-96 rounded-2xl bg-gray-700" />
        </div>
      </div>
    </section>

    {/* Project Details Section Skeleton */}
    <section className="py-16 bg-white dark:bg-gray-800">
      <div className="container-fmc">
        <Skeleton className="h-10 w-96 mb-8" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-gray-50 dark:bg-gray-900 rounded-xl p-6">
              <Skeleton className="h-6 w-32 mb-3" />
              <Skeleton className="h-5 w-3/4" />
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Gallery Section Skeleton */}
    <section className="py-16 bg-gray-50 dark:bg-gray-900">
      <div className="container-fmc">
        <Skeleton className="h-10 w-64 mb-8 mx-auto" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="w-full h-64 rounded-xl" />
          ))}
        </div>
      </div>
    </section>
  </div>
);

// Skeleton for About page
export const AboutPageSkeleton: React.FC = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900" style={{ paddingTop: 'var(--header-offset, 96px)' }}>
    {/* Hero Section */}
    <section className="relative py-20 bg-gradient-to-r from-blue-900 to-purple-900">
      <div className="container-fmc text-center">
        <Skeleton className="h-16 w-3/4 mx-auto mb-6 bg-gray-700" />
        <Skeleton className="h-8 w-2/3 mx-auto bg-gray-700" />
      </div>
    </section>

    {/* Company Story */}
    <section className="py-16 bg-white dark:bg-gray-800">
      <div className="container-fmc">
        <div className="grid lg:grid-cols-2 gap-12">
          <div className="space-y-4">
            <Skeleton className="h-10 w-3/4 mb-6" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-5/6" />
          </div>
          <Skeleton className="w-full h-96 rounded-2xl" />
        </div>
      </div>
    </section>

    {/* Mission, Vision, Values */}
    <section className="py-16 bg-gray-50 dark:bg-gray-900">
      <div className="container-fmc">
        <div className="grid md:grid-cols-3 gap-8">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl p-8">
              <Skeleton className="w-16 h-16 rounded-full mx-auto mb-4" />
              <Skeleton className="h-8 w-32 mx-auto mb-4" />
              <Skeleton className="h-5 w-full mb-2" />
              <Skeleton className="h-5 w-5/6 mx-auto" />
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Leadership Section */}
    <section className="py-16 bg-white dark:bg-gray-800">
      <div className="container-fmc">
        <Skeleton className="h-12 w-96 mx-auto mb-12" />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-gray-50 dark:bg-gray-900 rounded-2xl overflow-hidden">
              <Skeleton className="w-full h-64" />
              <div className="p-6">
                <Skeleton className="h-6 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  </div>
);

// Skeleton for listing pages (Sectors, Our Services, Industries Sectors)
export const ListingPageSkeleton: React.FC = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900" style={{ paddingTop: 'var(--header-offset, 96px)' }}>
    {/* Hero Section */}
    <section className="relative py-20 bg-gradient-to-r from-blue-900 to-purple-900">
      <div className="container-fmc text-center">
        <Skeleton className="h-16 w-3/4 mx-auto mb-6 bg-gray-700" />
        <Skeleton className="h-8 w-2/3 mx-auto bg-gray-700" />
      </div>
    </section>

    {/* Cards Grid */}
    <section className="py-16 bg-white dark:bg-gray-800">
      <div className="container-fmc">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg">
              <Skeleton className="w-16 h-16 rounded-xl mx-auto mb-6" />
              <Skeleton className="h-6 w-3/4 mx-auto mb-4" />
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-5/6 mx-auto mb-6" />
              <Skeleton className="h-10 w-32 mx-auto rounded-xl" />
            </div>
          ))}
        </div>
      </div>
    </section>
  </div>
);

// Skeleton for Projects listing page
export const ProjectsPageSkeleton: React.FC = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900" style={{ paddingTop: 'var(--header-offset, 96px)' }}>
    {/* Hero Section */}
    <section className="relative py-20 bg-gradient-to-r from-blue-900 to-purple-900">
      <div className="container-fmc text-center">
        <Skeleton className="h-16 w-3/4 mx-auto mb-6 bg-gray-700" />
        <Skeleton className="h-8 w-2/3 mx-auto bg-gray-700" />
      </div>
    </section>

    {/* Filters Section */}
    <section className="py-8 bg-white dark:bg-gray-800">
      <div className="container-fmc">
        <div className="grid md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-12 w-full rounded-xl" />
          ))}
        </div>
      </div>
    </section>

    {/* Projects Grid */}
    <section className="py-16 bg-gray-50 dark:bg-gray-900">
      <div className="container-fmc">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-lg">
              <Skeleton className="w-full h-64" />
              <div className="p-6">
                <Skeleton className="h-6 w-3/4 mb-3" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-5/6 mb-4" />
                <Skeleton className="h-10 w-full rounded-xl" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  </div>
);

// Skeleton for Documents/Careers pages (similar structure)
export const CareerDocumentPageSkeleton: React.FC = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900" style={{ paddingTop: 'var(--header-offset, 96px)' }}>
    {/* Hero Section */}
    <section className="relative py-20 bg-gradient-to-r from-blue-900 to-purple-900">
      <div className="container-fmc text-center">
        <Skeleton className="h-16 w-3/4 mx-auto mb-6 bg-gray-700" />
        <Skeleton className="h-8 w-2/3 mx-auto bg-gray-700" />
      </div>
    </section>

    {/* Filters/Search Section */}
    <section className="py-8 bg-white dark:bg-gray-800">
      <div className="container-fmc">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <Skeleton className="h-12 w-full md:w-96 rounded-xl" />
          <div className="flex gap-2">
            <Skeleton className="h-10 w-10 rounded-xl" />
            <Skeleton className="h-10 w-10 rounded-xl" />
          </div>
        </div>
      </div>
    </section>

    {/* Items Grid */}
    <section className="py-16 bg-gray-50 dark:bg-gray-900">
      <div className="container-fmc">
        <div className="space-y-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <Skeleton className="h-6 w-3/4 mb-3" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-5/6" />
                </div>
                <Skeleton className="h-10 w-32 rounded-xl" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  </div>
);

// Skeleton for Organization Chart page
export const OrganizationChartSkeleton: React.FC = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900" style={{ paddingTop: 'var(--header-offset, 96px)' }}>
    {/* Hero Section */}
    <section className="relative py-20 bg-gradient-to-r from-blue-900 to-purple-900">
      <div className="container-fmc text-center">
        <Skeleton className="h-16 w-3/4 mx-auto mb-6 bg-gray-700" />
        <Skeleton className="h-8 w-2/3 mx-auto bg-gray-700" />
      </div>
    </section>

    {/* Organization Chart Structure */}
    <section className="py-16 bg-white dark:bg-gray-800">
      <div className="container-fmc">
        {/* General Manager */}
        <div className="flex justify-center mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg" style={{ width: '300px' }}>
            <Skeleton className="w-24 h-24 rounded-full mx-auto mb-4" />
            <Skeleton className="h-6 w-3/4 mx-auto mb-2" />
            <Skeleton className="h-4 w-1/2 mx-auto" />
          </div>
        </div>

        {/* Deputy Managers */}
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
              <Skeleton className="w-20 h-20 rounded-full mx-auto mb-4" />
              <Skeleton className="h-6 w-3/4 mx-auto mb-2" />
              <Skeleton className="h-4 w-1/2 mx-auto" />
            </div>
          ))}
        </div>

        {/* Departments */}
        <div className="grid md:grid-cols-4 gap-6">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow">
              <Skeleton className="h-5 w-3/4 mx-auto mb-2" />
              <Skeleton className="h-4 w-1/2 mx-auto" />
            </div>
          ))}
        </div>
      </div>
    </section>
  </div>
);

// Skeleton for policy/legal pages (Privacy, Terms, Cookie, SiteMap)
export const LegalPageSkeleton: React.FC = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900" style={{ paddingTop: 'var(--header-offset, 96px)' }}>
    {/* Hero Section */}
    <section className="relative py-20 bg-gradient-to-r from-blue-900 to-purple-900">
      <div className="container-fmc text-center">
        <Skeleton className="w-20 h-20 rounded-2xl mx-auto mb-6 bg-gray-700" />
        <Skeleton className="h-16 w-3/4 mx-auto mb-6 bg-gray-700" />
        <Skeleton className="h-8 w-2/3 mx-auto bg-gray-700" />
      </div>
    </section>

    {/* Content Section */}
    <section className="py-16 bg-white dark:bg-gray-800">
      <div className="container-fmc max-w-4xl">
        <Skeleton className="h-6 w-48 mb-8" />
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="mb-12">
            <Skeleton className="h-8 w-64 mb-4" />
            <div className="space-y-2">
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-5/6" />
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-4/5" />
            </div>
          </div>
        ))}
      </div>
    </section>
  </div>
);

export const HomeSkeleton: React.FC = () => (
  <div>
    {/* Hero Carousel Skeleton */}
    <section className="relative min-h-screen overflow-hidden bg-gray-900" style={{ paddingTop: 'var(--header-offset, 96px)' }}>
      <div className="relative w-full" style={{ height: 'calc(100vh - var(--header-offset, 96px))' }}>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="container-fmc px-4 text-center">
            <Skeleton className="h-20 w-3/4 mx-auto mb-6 bg-gray-700" />
            <Skeleton className="h-8 w-2/3 mx-auto bg-gray-700" />
          </div>
        </div>
        {/* Navigation Dots */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="w-4 h-4 rounded-full bg-gray-700" />
          ))}
        </div>
      </div>
    </section>

    {/* Features Section Skeleton */}
    <section className="py-20 bg-white dark:bg-gray-900">
      <div className="container-fmc">
        <div className="text-center mb-16">
          <Skeleton className="h-12 w-96 mx-auto mb-4" />
          <Skeleton className="h-6 w-2/3 mx-auto" />
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
              <Skeleton className="w-full h-48 rounded-xl mb-4" />
              <Skeleton className="h-6 w-3/4 mb-3" />
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-5/6" />
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Services Carousel Skeleton */}
    <section className="py-20 bg-gray-50 dark:bg-gray-800">
      <div className="container-fmc">
        <div className="text-center mb-16">
          <Skeleton className="h-12 w-96 mx-auto mb-4" />
          <Skeleton className="h-6 w-2/3 mx-auto" />
        </div>
        <div className="relative">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white dark:bg-gray-700 rounded-2xl overflow-hidden shadow-lg">
                <Skeleton className="w-full h-64" />
                <div className="p-6">
                  <Skeleton className="h-6 w-3/4 mb-3" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-5/6 mb-4" />
                  <Skeleton className="h-10 w-full rounded-xl" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* Sectors & Industries Skeleton */}
    <section className="py-20 bg-white dark:bg-gray-900">
      <div className="container-fmc">
        <div className="text-center mb-16">
          <Skeleton className="h-12 w-96 mx-auto mb-4" />
          <Skeleton className="h-6 w-2/3 mx-auto" />
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
              <Skeleton className="w-16 h-16 rounded-xl mb-4" />
              <Skeleton className="h-6 w-3/4 mb-3" />
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-5/6" />
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Company Profile Skeleton */}
    <section className="py-20 bg-blue-50 dark:bg-blue-900/20">
      <div className="container-fmc">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <Skeleton className="h-10 w-3/4 mb-4" />
            <Skeleton className="h-6 w-full mb-2" />
            <Skeleton className="h-6 w-5/6 mb-4" />
            <div className="grid grid-cols-2 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-12 w-24" />
                  <Skeleton className="h-4 w-32" />
                </div>
              ))}
            </div>
          </div>
          <Skeleton className="w-full h-96 rounded-2xl" />
        </div>
      </div>
    </section>

    {/* Featured Projects Skeleton */}
    <section className="py-20 bg-white dark:bg-gray-900">
      <div className="container-fmc">
        <div className="text-center mb-16">
          <Skeleton className="h-12 w-96 mx-auto mb-4" />
          <Skeleton className="h-6 w-2/3 mx-auto" />
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-lg">
              <Skeleton className="w-full h-64" />
              <div className="p-6">
                <Skeleton className="h-6 w-3/4 mb-3" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-5/6 mb-4" />
                <Skeleton className="h-10 w-full rounded-xl" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CEO Testimonial Skeleton */}
    <section className="py-20 bg-gradient-to-r from-blue-900 to-purple-900">
      <div className="container-fmc">
        <div className="max-w-4xl mx-auto text-center">
          <Skeleton className="w-24 h-24 rounded-full mx-auto mb-6 bg-gray-700" />
          <Skeleton className="h-6 w-3/4 mx-auto mb-4 bg-gray-700" />
          <Skeleton className="h-6 w-2/3 mx-auto mb-2 bg-gray-700" />
          <Skeleton className="h-6 w-1/2 mx-auto mb-6 bg-gray-700" />
          <Skeleton className="h-5 w-48 mx-auto bg-gray-700" />
        </div>
      </div>
    </section>

    {/* Clients Logos Skeleton */}
    <section className="py-20 bg-white dark:bg-gray-900">
      <div className="container-fmc">
        <div className="text-center mb-12">
          <Skeleton className="h-10 w-96 mx-auto mb-4" />
          <Skeleton className="h-5 w-2/3 mx-auto" />
        </div>
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-8">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => (
            <Skeleton key={i} className="w-full h-24 rounded-xl" />
          ))}
        </div>
      </div>
    </section>

    {/* News Section Skeleton */}
    <section className="py-20 bg-gray-50 dark:bg-gray-800">
      <div className="container-fmc">
        <div className="text-center mb-16">
          <Skeleton className="h-12 w-96 mx-auto mb-4" />
          <Skeleton className="h-6 w-2/3 mx-auto" />
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[1, 2, 3].map((i) => (
            <article key={i} className="bg-white dark:bg-gray-700 rounded-3xl overflow-hidden shadow-lg">
              <Skeleton className="w-full h-56" />
              <div className="p-6">
                <Skeleton className="h-5 w-24 rounded-full mb-3" />
                <Skeleton className="h-6 w-3/4 mb-3" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-5/6 mb-4" />
                <div className="flex items-center justify-between">
                  <Skeleton className="h-10 w-28 rounded-xl" />
                  <Skeleton className="h-4 w-24" />
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>

    {/* Vendors Logos Skeleton */}
    <section className="py-20 bg-white dark:bg-gray-900">
      <div className="container-fmc">
        <div className="text-center mb-12">
          <Skeleton className="h-10 w-96 mx-auto mb-4" />
          <Skeleton className="h-5 w-2/3 mx-auto" />
        </div>
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-8">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => (
            <Skeleton key={i} className="w-full h-24 rounded-xl" />
          ))}
        </div>
      </div>
    </section>

    {/* Social Feed Skeleton */}
    <section className="py-20 bg-gray-50 dark:bg-gray-800">
      <div className="container-fmc">
        <div className="text-center mb-12">
          <Skeleton className="h-10 w-96 mx-auto mb-4" />
          <Skeleton className="h-5 w-2/3 mx-auto" />
        </div>
        <Skeleton className="w-full h-96 rounded-2xl" />
      </div>
    </section>

    {/* Subscribe Section Skeleton */}
    <section className="py-20 bg-gradient-to-r from-blue-900 to-purple-900">
      <div className="container-fmc">
        <div className="max-w-2xl mx-auto text-center">
          <Skeleton className="h-10 w-96 mx-auto mb-4 bg-gray-700" />
          <Skeleton className="h-5 w-3/4 mx-auto mb-8 bg-gray-700" />
          <div className="flex flex-col md:flex-row gap-4 justify-center">
            <Skeleton className="h-12 w-full md:w-96 rounded-xl bg-gray-700" />
            <Skeleton className="h-12 w-full md:w-32 rounded-xl bg-gray-700" />
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default Skeleton;
